import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useGestor } from '@/contexts/GestorContext'
import { toast } from 'sonner'
import { AlertTriangle } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { VW_ORCAMENTO_V2, num, percentUsoOrcado, type OrcamentoV2Linha } from '@/lib/gestor-orcamento-v2'

const LIMIAR = 0.8

export default function GestorAlertasPage() {
  const { areaSelecionada, anoSelecionado } = useGestor()
  const [rows, setRows] = useState<OrcamentoV2Linha[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    if (!anoSelecionado || !areaSelecionada) return
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from(VW_ORCAMENTO_V2)
        .select('*')
        .eq('ano', parseInt(anoSelecionado, 10))
        .eq('centro_resultado', areaSelecionada)

      if (error) throw error
      setRows((data as OrcamentoV2Linha[]) || [])
    } catch (e) {
      console.error(e)
      toast.error('Erro ao carregar dados para alertas.')
    } finally {
      setLoading(false)
    }
  }, [anoSelecionado, areaSelecionada])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const alertas = useMemo(() => {
    return rows
      .map((r) => {
        const o = num(r.valor_total_orcado)
        const pct = percentUsoOrcado(r)
        return { row: r, orcado: o, pctUso: pct * 100, comprometido: num(r.valor_comprometido) }
      })
      .filter((x) => x.orcado > 0 && x.pctUso >= LIMIAR * 100)
      .sort((a, b) => b.pctUso - a.pctUso)
  }, [rows])

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#131E29]/20 border-t-orange-500 border-r-[#131E29]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Card className="border-amber-200 bg-amber-50/40 shadow-sm">
        <CardHeader className="flex flex-row items-start gap-3 pb-2">
          <div className="mt-0.5 rounded-lg bg-amber-100 p-2">
            <AlertTriangle className="h-5 w-5 text-amber-700" />
          </div>
          <div>
            <CardTitle className="text-base text-amber-950">Resumo</CardTitle>
            <CardDescription className="text-amber-900/80">
              {alertas.length === 0
                ? 'Nenhuma linha acima de 80% do orçado no período selecionado.'
                : `${alertas.length} linha(s) com uso igual ou superior a 80% do orçado.`}
            </CardDescription>
          </div>
        </CardHeader>
      </Card>

      <Card className="border-none shadow-sm bg-white">
        <CardHeader>
          <CardTitle className="text-base">Linhas em alerta</CardTitle>
          <CardDescription>
            Área {areaSelecionada} · {anoSelecionado}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50/80">
                <TableRow>
                  <TableHead className="pl-4">Macro</TableHead>
                  <TableHead>Classificação</TableHead>
                  <TableHead>Subtipo</TableHead>
                  <TableHead className="text-right">Orçado</TableHead>
                  <TableHead className="text-right">Comprometido</TableHead>
                  <TableHead className="text-right">% uso</TableHead>
                  <TableHead className="pr-4">Gravidade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alertas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-14">
                      Sem alertas para os filtros atuais.
                    </TableCell>
                  </TableRow>
                ) : (
                  alertas.map(({ row, orcado, pctUso, comprometido }, idx) => (
                    <TableRow key={row.chave_unica_v2 ?? `alerta-${idx}`} className="border-slate-50">
                      <TableCell className="pl-4 text-sm max-w-[140px] truncate" title={row.tipo_receita_despesa || ''}>
                        {row.tipo_receita_despesa || '—'}
                      </TableCell>
                      <TableCell className="text-sm max-w-[140px] truncate" title={row.classificacao_receita_despesa || ''}>
                        {row.classificacao_receita_despesa || '—'}
                      </TableCell>
                      <TableCell className="text-sm max-w-[160px] truncate" title={row.subtipo_despesa || ''}>
                        {row.subtipo_despesa || '—'}
                      </TableCell>
                      <TableCell className="text-right text-sm">{formatCurrency(orcado)}</TableCell>
                      <TableCell className="text-right text-sm">{formatCurrency(comprometido)}</TableCell>
                      <TableCell className="text-right text-sm font-semibold text-amber-800">
                        {pctUso.toFixed(1)}%
                      </TableCell>
                      <TableCell className="pr-4">
                        <Badge
                          variant="outline"
                          className={
                            pctUso >= 100
                              ? 'border-red-300 bg-red-50 text-red-800'
                              : 'border-amber-300 bg-amber-50 text-amber-900'
                          }
                        >
                          {pctUso >= 100 ? 'Estourado' : 'Alto uso'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
